import {
  Alert,
  Box,
  Button,
  ListItemText,
  Menu,
  MenuItem,
  MenuList,
  SvgIcon,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { DataGrid } from '@mui/x-data-grid';
import { PeopleSceneContext } from 'components/@home/@people/PeopleSceneContext';
import Loading from 'components/common/Loading';
import Pagination from 'components/controls/Pagination';
import DownloadIcon from 'mdi-react/FileDownloadOutlineIcon';
import { arrayOf, func, object } from 'prop-types';
import React, { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { I18n } from 'utils/i18n';
import { apiUrl } from 'services/apiBaseUrl';
import employeeShape from 'shapes/employee';
import { ImportStatus } from 'store/app/entities/employeeImports/reducer';
import populateEmployees from 'store/selectors/populateEmployees';
import renderDateTimeCell from 'utils/renderDateTimeCell';
import Toolbar from './Toolbar';

const getStyles = theme => ({
  root: {
    width: '100%',
    height: '100vh',
    overflowX: 'auto',
    display: 'flex',
    flexDirection: 'column',
    p: '20px',
  },
  tableWrapper: {
    overflowX: 'auto',
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  intro: {
    display: 'flex',
    flexDirection: 'row',
    mt: 3,
    mb: 4,
    '& a': {
      fontWeight: 500,
      color: theme.palette.primary.main,
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
    },
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'center',
    width: `calc(50% - ${theme.spacing(1)})`,
    '& + &': {
      ml: 2,
    },
  },
  icon: {
    fontSize: 18,
    mr: '5px',
    color: theme.palette.primary.main,
  },
  infoBox: {
    background: theme.palette.secondary.ultraUltraLight,
    border: `1px solid ${theme.palette.secondary.extraLight}`,
    borderRadius: '6px',
    p: 4,
    '& ol': {
      padding: '0 0 0 15px',
      listStylePosition: 'outside',
      marginBottom: 0,
      color: theme.palette.secondary.main,
      fontSize: '0.8125rem',
      lineHeight: 1.47,
    },
  },
  characteristic: {
    color: theme.palette.black,
  },
  downloadLinks: {
    display: 'inline-flex',
    minWidth: 450,
    flexDirection: 'row',
    justifyContent: 'space-between',
    mt: 2,
  },
  statusCell: {
    display: 'flex',
    gap: 1,
    alignItems: 'center',
  },
  menuItem: {
    minHeight: 0,
    borderBottom: 0,
    paddingTop: 0,
    paddingBottom: 0,
    '& .MuiTypography-root': {
      fontWeight: 500,
    },
  },
  alert: {
    mb: 2,
  },
  actions: {
    mt: 1,
    '& button': {
      p: 1,
      paddingTop: '2px',
      paddingBottom: '2px',
      minHeight: 0,
    },
    '& button + button': {
      ml: 2,
    },
  },
  download: {
    fontWeight: 500,
    color: theme.palette.primary.main,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  button: {
    paddingBottom: '2px',
    paddingTop: '2px',
    minHeight: 0,
    '& + &': {
      ml: 2,
    },
  },
});

const DetailsLink = ({ row, onClick, styles }) => {
  const anchorEl = useRef();
  const [isOpen, setOpen] = useState(false);
  const toggle = useCallback(() => setOpen(o => !o), []);
  const close = useCallback(() => setOpen(false), []);
  return (
    <>
      <Button ref={anchorEl} onClick={toggle} style={{ fontWeight: 500 }} variant="text">
        {I18n.t('ImportsScene.ImportsTable.Details')}
      </Button>
      <Menu
        open={isOpen}
        anchorEl={anchorEl.current}
        onClose={close}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        disableRestoreFocus
      >
        <MenuItem disabled>{I18n.t('ImportsScene.Result')}</MenuItem>
        <MenuList sx={{ width: 200, maxWidth: '100%' }}>
          {['created', 'updated', 'skipped'].map(status => (
            <MenuItem dense key={status} onClick={() => onClick(status)}>
              <ListItemText sx={styles.menuItem}>
                {I18n.t(`ImportsScene.ResultStatuses.${status}`)}
              </ListItemText>
              <Typography
                variant="body2"
                sx={{
                  display: 'inline',
                  color: 'text.secondary',
                }}
              >
                {row.result[status]?.length || 0}
              </Typography>
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
    </>
  );
};

DetailsLink.propTypes = {
  row: object.isRequired,
  onClick: func.isRequired,
  styles: object.isRequired,
};

const ImportsTable = ({ onDiscard, employeeImports, onContinue, onClickShowResults }) => {
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const employees = useSelector(populateEmployees);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const [sortModel, setSortModel] = useState([{ field: 'createdAt', sort: 'desc' }]);

  const { downloadReport } = useContext(PeopleSceneContext);

  const columns = [
    {
      field: 'createdAt',
      type: 'string',
      flex: 1,
      editable: false,
      headerName: I18n.t('ImportsScene.ImportsTable.Date'),
      renderCell: renderDateTimeCell,
    },
    {
      field: 'fileName',
      flex: 2,
      editable: false,
      headerName: I18n.t('ImportsScene.ImportsTable.FileName'),
      renderCell: params =>
        params.row.isExpired ? (
          <Tooltip title={I18n.t('ImportsScene.ImportsTable.FileNotAvailable')}>
            <Typography>{params.value}</Typography>
          </Tooltip>
        ) : (
          <Typography>
            <Box
              component="a"
              href={apiUrl(`/v1/employee-import/${params.id}/d/${params.value}`)}
              download={params.value}
              sx={styles.download}
            >
              {params.value}
              <SvgIcon sx={styles.icon}>
                <DownloadIcon />
              </SvgIcon>
            </Box>
          </Typography>
        ),
    },
    {
      field: 'creator',
      flex: 1,
      editable: false,
      headerName: I18n.t('ImportsScene.ImportsTable.User'),
      valueGetter: value => {
        const creator = Object.values(employees).find(e => e.user?._id === value);
        return creator ? `${creator.firstName} ${creator.surName}` : `(${I18n.t('Deleted user')})`;
      },
    },
    {
      field: 'status',
      type: 'singleSelect',
      valueOptions: ['uploaded', 'importing', 'finished'],
      valueFormatter: value => I18n.t(`ImportsScene.ImportsTable.Statuses.${value}`),
      flex: 1,
      headerName: I18n.t('ImportsScene.ImportsTable.Status'),
      renderCell: params => (
        <Box sx={styles.statusCell}>
          <Typography>{params.formattedValue}</Typography>
          {params.value === 'importing' && <Loading size={14} />}
        </Box>
      ),
    },
    {
      field: 'action',
      flex: 1,
      disablePadding: false,
      headerName: '',
      renderCell: params => {
        const notActive = params.row.status === ImportStatus.uploaded;
        const finished = params.row.status === ImportStatus.finished;
        return (
          <Box sx={{ display: 'flex', flexDirection: 'row', height: 20, alignItems: 'center' }}>
            {notActive && (
              <Button
                onClick={() => onContinue(params.row)}
                variant="contained"
                color="primary"
                sx={styles.button}
                size="small"
              >
                {I18n.t('ImportsScene.Continue')}
              </Button>
            )}
            {notActive && (
              <Button
                onClick={() => onDiscard(params.row)}
                variant="outlined"
                color="secondary"
                sx={styles.button}
                size="small"
              >
                {I18n.t('ImportsScene.Discard')}
              </Button>
            )}
            {finished && (
              <DetailsLink
                row={params.row}
                onClick={status => onClickShowResults(params.row, status)}
                styles={styles}
              />
            )}
          </Box>
        );
      },
    },
  ];

  const changePage = ({ selected }) => {
    setPage(selected);
  };

  const changeRowsPerPage = e => {
    setRowsPerPage(e.target.value);
  };

  const importsCount = employeeImports.length;
  const pendingImport = employeeImports?.find(ei => ei.status === 'uploaded');

  const handleDeletePending = () => onDiscard(pendingImport);
  const handleContinue = imp => onContinue(imp);
  const continuePending = () => handleContinue(pendingImport);

  const rows = employeeImports.map(r => {
    return {
      ...r,
      id: r._id,
      createdAt: new Date(r.createdAt),
    };
  });

  return (
    <Box sx={styles.root}>
      <Box sx={styles.tableWrapper}>
        <Typography component="div" variant="h5">
          {I18n.t('ImportsScene.ImportsTable.Imports')}
        </Typography>
        <Box sx={styles.intro}>
          <Box sx={styles.column}>
            <Typography
              sx={{
                whiteSpace: 'pre-wrap',
              }}
            >
              {I18n.t('ImportsScene.ImportsTable.Intro')}
            </Typography>
            <Box sx={styles.downloadLinks}>
              <Typography
                sx={{
                  display: 'inline',
                }}
              >
                <Button
                  onClick={() => downloadReport(true)}
                  endIcon={
                    <SvgIcon sx={styles.icon}>
                      <DownloadIcon />
                    </SvgIcon>
                  }
                >
                  {I18n.t('ImportsScene.ImportsTable.Download template')}
                </Button>
              </Typography>
              <Typography
                sx={{
                  display: 'inline',
                }}
              >
                <Button
                  onClick={() => downloadReport(false)}
                  endIcon={
                    <SvgIcon sx={styles.icon}>
                      <DownloadIcon />
                    </SvgIcon>
                  }
                >
                  {I18n.t('ImportsScene.ImportsTable.Download current database')}
                </Button>
              </Typography>
            </Box>
          </Box>
          <Box sx={styles.column}>
            <Box sx={styles.infoBox}>
              <Typography>
                <strong>{I18n.t('ImportsScene.ImportsTable.How does import')}</strong>
              </Typography>
              <ol>
                {[1, 2, 3, 4].map(char => (
                  <li key={`char.${char}`}>
                    <Typography sx={styles.characteristic}>
                      {I18n.t(`ImportsScene.ImportsTable.ImportSteps.step${char}`)}
                    </Typography>
                  </li>
                ))}
              </ol>
            </Box>
          </Box>
        </Box>
        <Toolbar total={importsCount} pendingImport={pendingImport} />
        {pendingImport && (
          <Alert severity="warning" sx={styles.alert}>
            <Typography>{I18n.t('ImportsScene.You have unfinished')}</Typography>
            <Box sx={styles.actions}>
              <Button variant="contained" color="primary" size="small" onClick={continuePending}>
                {I18n.t('ImportsScene.Continue')}
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                size="small"
                onClick={handleDeletePending}
              >
                {I18n.t('ImportsScene.Discard')}
              </Button>
            </Box>
          </Alert>
        )}
        <DataGrid
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          columns={columns}
          rows={rows}
          autoPageSize
          onPageSizeChange={setRowsPerPage}
          getRowId={m => m._id}
          getRowHeight={() => 48}
          columnHeaderHeight={48}
          paginationModel={{ page, pageSize: rowsPerPage }}
          onPaginationModelChange={({ page: newPage, pageSize: newPageSize }) => {
            setPage(newPage);
            setRowsPerPage(newPageSize);
          }}
          slots={{ footer: Pagination }}
          slotProps={{
            footer: {
              page,
              perPage: rowsPerPage,
              total: importsCount,
              onChange: changePage,
              onChangePerPage: changeRowsPerPage,
            },
          }}
        />
      </Box>
    </Box>
  );
};

ImportsTable.propTypes = {
  employeeImports: arrayOf(employeeShape).isRequired,
  onDiscard: func.isRequired,
  onContinue: func.isRequired,
  onClickShowResults: func.isRequired,
};

export default ImportsTable;
